import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Sparkle, Trash, MagicWand, DotsSixVertical, CalendarPlus, X, ClipboardText,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { taskColor, fmtMinutes } from "@/components/schedule/utils";
import { toast } from "sonner";

export function TaskPanel({ tasks = [], setDragItem, onCreate, onDelete, onAutoSchedule, autoSchedulingId, suggestFn }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [total, setTotal] = useState(120);
  const [size, setSize] = useState(30);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  const reset = () => { setTitle(""); setDesc(""); setTotal(120); setSize(30); setSuggestion(null); setOpen(false); };

  const suggest = async () => {
    if (!title.trim()) { toast.error("Add a task title first"); return; }
    setSuggesting(true);
    try {
      const d = await suggestFn(title, desc);
      setSuggestion(d);
      setTotal(d.total_minutes);
      setSize(d.block_size);
      toast.success("Auppo suggested a breakdown");
    } catch { toast.error("Could not generate a breakdown"); }
    finally { setSuggesting(false); }
  };

  const save = () => {
    if (!title.trim()) { toast.error("Add a task title"); return; }
    onCreate({ title, description: desc, total_minutes: Number(total), block_size: size });
    reset();
  };

  return (
    <div className="w-80 shrink-0 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400">Backlog</div>
          <h3 className="font-heading font-semibold text-lg">Tasks</h3>
        </div>
        <Button
          data-testid="new-task-btn"
          onClick={() => setOpen((o) => !o)}
          size="sm"
          className="rounded-xl bg-slate-900 hover:bg-slate-800 gap-1.5"
        >
          {open ? <X size={15} /> : <Plus size={15} />} {open ? "Close" : "New task"}
        </Button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="bg-white border border-border rounded-2xl p-4 space-y-3 overflow-hidden"
          >
            <Input data-testid="task-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Task title" className="rounded-xl" />
            <Textarea data-testid="task-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Describe the task so Auppo can break it into blocks…" className="rounded-xl min-h-[70px] text-sm resize-none" />
            <Button data-testid="ai-breakdown-btn" onClick={suggest} disabled={suggesting} variant="outline" className="w-full rounded-xl border-slate-300 gap-2">
              <MagicWand weight="fill" size={15} /> {suggesting ? "Thinking…" : "Ask Auppo to break it down"}
            </Button>

            {suggestion && (
              <div className="bg-slate-50 border border-border rounded-xl p-3 space-y-1.5" data-testid="suggestion-box">
                <div className="text-[11px] uppercase tracking-wider font-bold text-slate-400 flex items-center gap-1"><Sparkle weight="fill" size={11} /> Suggested blocks</div>
                {suggestion.blocks.map((b, i) => (
                  <div key={i} className="flex items-center justify-between text-sm text-slate-600">
                    <span className="truncate">{b.label}</span>
                    <span className="text-xs text-slate-400 shrink-0 ml-2">{b.minutes}m</span>
                  </div>
                ))}
                {suggestion.rationale && <div className="text-xs text-slate-400 pt-1 italic">{suggestion.rationale}</div>}
              </div>
            )}

            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="text-[11px] text-slate-400 font-medium">Total time (min)</label>
                <Input data-testid="task-total-input" type="number" min={30} step={30} value={total} onChange={(e) => setTotal(e.target.value)} className="rounded-xl h-9 mt-1" />
              </div>
              <div>
                <label className="text-[11px] text-slate-400 font-medium">Block size</label>
                <div className="flex gap-1 mt-1">
                  {[30, 60].map((s) => (
                    <button
                      key={s}
                      data-testid={`block-size-${s}`}
                      onClick={() => setSize(s)}
                      className={`h-9 px-3 rounded-xl text-sm font-medium border transition-colors ${size === s ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}
                    >{s}m</button>
                  ))}
                </div>
              </div>
            </div>
            <Button data-testid="save-task-btn" onClick={save} className="w-full rounded-xl bg-slate-900 hover:bg-slate-800 gap-2">
              <Plus size={15} /> Add task
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="space-y-3">
        {tasks.length === 0 && !open && (
          <div className="text-center py-10 text-slate-400 border border-dashed border-slate-200 rounded-2xl" data-testid="tasks-empty">
            <ClipboardText size={30} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No tasks yet.</p>
            <p className="text-xs">Add one, then drag or auto-schedule it.</p>
          </div>
        )}
        {tasks.map((t) => {
          const c = taskColor(t.color);
          const pct = t.total_minutes ? Math.min(100, Math.round((t.scheduled_minutes / t.total_minutes) * 100)) : 0;
          const remaining = t.remaining_minutes;
          return (
            <motion.div
              key={t.id}
              layout
              draggable
              onDragStart={() => setDragItem({ type: "task", id: t.id })}
              onDragEnd={() => setDragItem(null)}
              data-testid={`task-card-${t.id}`}
              className="bg-white border border-border rounded-2xl p-4 cursor-grab active:cursor-grabbing hover:shadow-sm hover:-translate-y-[1px] transition-all"
            >
              <div className="flex items-start gap-2">
                <DotsSixVertical size={16} className="text-slate-300 mt-0.5 shrink-0" />
                <span className={`h-2.5 w-2.5 rounded-full mt-1.5 shrink-0 ${c.solid}`} />
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-900 text-sm leading-snug">{t.title}</div>
                  {t.description && <div className="text-xs text-slate-400 truncate mt-0.5">{t.description}</div>}
                </div>
                <button data-testid={`delete-task-${t.id}`} onClick={() => onDelete(t.id)} className="text-slate-300 hover:text-rose-600 shrink-0">
                  <Trash size={15} />
                </button>
              </div>

              <div className="mt-3">
                <div className="flex items-center justify-between text-[11px] text-slate-400 mb-1">
                  <span>{fmtMinutes(t.scheduled_minutes)} / {fmtMinutes(t.total_minutes)} scheduled</span>
                  <span className="font-medium text-slate-500">{t.block_size}m blocks</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full ${c.solid}`} style={{ width: `${pct}%` }} />
                </div>
              </div>

              <div className="flex items-center gap-2 mt-3">
                <Button
                  data-testid={`auto-schedule-${t.id}`}
                  onClick={() => onAutoSchedule(t.id)}
                  disabled={remaining <= 0 || autoSchedulingId === t.id}
                  size="sm"
                  variant="outline"
                  className="rounded-lg text-xs gap-1.5 border-slate-300 flex-1"
                >
                  <CalendarPlus weight="bold" size={13} />
                  {autoSchedulingId === t.id ? "Placing…" : remaining <= 0 ? "Fully scheduled" : `Auto-schedule ${fmtMinutes(remaining)}`}
                </Button>
              </div>
              <div className="text-[10px] text-slate-300 mt-2 text-center">drag onto the grid to place a {t.block_size}m block</div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
